import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  QrCode,
  RefreshCw,
  Trash2,
  Download,
  Copy,
  Check,
  AlertTriangle,
  Shield,
  Eye,
  Loader2,
} from 'lucide-react';
import { useSite } from '../../context/SiteContext';

const PublicReporting = ({ user }) => {
  const { activeSiteId, activeSiteName } = useSite();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Token state
  const [tokenStatus, setTokenStatus] = useState(null);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [publicUrl, setPublicUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const canvasRef = useRef(null);

  const fetchTokenStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tenant/public-reporting/token/status`);
      setTokenStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch token status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokenStatus();
  }, [fetchTokenStatus]);

  // Generate QR code on canvas whenever publicUrl changes
  useEffect(() => {
    if (!publicUrl) {
      setQrDataUrl(null);
      return;
    }

    QRCode.toDataURL(publicUrl, {
      width: 400,
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' },
      errorCorrectionLevel: 'M',
    })
      .then((dataUrl) => setQrDataUrl(dataUrl))
      .catch((err) => console.error('QR generation failed:', err));
  }, [publicUrl]);

  const handleGenerate = async () => {
    if (!activeSiteId) {
      setError('Please select a site from the header before generating a QR token.');
      return;
    }

    setGenerating(true);
    setError('');
    setSuccess('');
    setGeneratedToken(null);
    setPublicUrl('');
    setQrDataUrl(null);

    try {
      const response = await axios.post(`${API}/tenant/public-reporting/token`, {
        site_id: activeSiteId,
        site_name: activeSiteName || null,
      });
      const data = response.data;
      setGeneratedToken(data.token);
      setPublicUrl(data.public_url);
      setSuccess('Token generated successfully. Copy the link or download the QR code below.');

      // Refresh status
      await fetchTokenStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate token');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('Are you sure you want to revoke the active token? The current QR code link will stop working.')) {
      return;
    }

    setRevoking(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete(`${API}/tenant/public-reporting/token`);
      setSuccess('Token revoked. The public reporting link is no longer active.');
      setGeneratedToken(null);
      setPublicUrl('');
      setQrDataUrl(null);
      await fetchTokenStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to revoke token');
    } finally {
      setRevoking(false);
    }
  };

  const handleCopyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = 'incident-report-qr.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <QrCode className="h-5 w-5 text-[#1B5C94]" />
          Anonymous Incident Reporting
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Generate a QR code that allows anyone to report safety incidents anonymously — no login required.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="border-green-200 bg-green-50">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Current Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Token Status
          </CardTitle>
          <CardDescription>
            Only one active token is allowed at a time. Generating a new token revokes the previous one.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : tokenStatus?.has_active_token ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Active
                </span>
                <span className="text-sm text-gray-500">
                  Created {tokenStatus.created_at ? new Date(tokenStatus.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Times used:</span>{' '}
                  <span className="font-medium">{tokenStatus.use_count || 0}</span>
                </div>
                <div>
                  <span className="text-gray-500">Last used:</span>{' '}
                  <span className="font-medium">
                    {tokenStatus.last_used_at
                      ? new Date(tokenStatus.last_used_at).toLocaleDateString()
                      : 'Never'}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={generating}
                >
                  {generating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  Regenerate Token
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRevoke}
                  disabled={revoking}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  {revoking ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-1" />
                  )}
                  Revoke Token
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                No active token. Generate one to enable anonymous incident reporting.
              </p>
              <Button onClick={handleGenerate} disabled={generating}>
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <QrCode className="h-4 w-4 mr-1" />
                )}
                Generate QR Code Token
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generated Token & QR Code (shown only after generation) */}
      {generatedToken && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Your New Token
            </CardTitle>
            <CardDescription>
              This token is shown only once. Copy the link or download the QR code now.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Copy Link */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Public Reporting Link</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white border rounded px-3 py-2 break-all select-all">
                  {publicUrl}
                </code>
                <Button variant="outline" size="sm" onClick={handleCopyLink}>
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* QR Code Image — generated client-side */}
            {qrDataUrl && (
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">QR Code</label>
                <div className="flex items-start gap-4">
                  <div className="border rounded-lg p-3 bg-white inline-block">
                    <img
                      src={qrDataUrl}
                      alt="QR Code for anonymous incident reporting"
                      className="w-48 h-48"
                    />
                  </div>
                  <div className="space-y-2">
                    <Button variant="outline" size="sm" onClick={handleDownloadQr}>
                      <Download className="h-4 w-4 mr-1" />
                      Download PNG
                    </Button>
                    <p className="text-xs text-gray-500">
                      Print this QR code and display it in common areas, safety boards, or near work zones.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Warning */}
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                Anyone with this link can submit incident reports to your organization.
                Keep it restricted to intended audiences. You can revoke it at any time.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm text-gray-600">
            <li className="flex gap-2">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1B5C94] text-white flex items-center justify-center text-xs font-medium">1</span>
              <span>Generate a token above — this creates a unique, secure link.</span>
            </li>
            <li className="flex gap-2">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1B5C94] text-white flex items-center justify-center text-xs font-medium">2</span>
              <span>Download or print the QR code and display it at your workplace.</span>
            </li>
            <li className="flex gap-2">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1B5C94] text-white flex items-center justify-center text-xs font-medium">3</span>
              <span>Anyone who scans the QR code can submit an incident report — no login needed.</span>
            </li>
            <li className="flex gap-2">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1B5C94] text-white flex items-center justify-center text-xs font-medium">4</span>
              <span>Admin users receive a high-priority notification for each anonymous report.</span>
            </li>
            <li className="flex gap-2">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1B5C94] text-white flex items-center justify-center text-xs font-medium">5</span>
              <span>Reports appear in your Incidents list with source marked as "Anonymous QR".</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicReporting;
